import { useMemo } from 'react';
import type { ComponentType, FC } from 'react';
import capitalize from 'lodash-es/capitalize';
import { ProductType } from '../../../../bff-api/baskets/model';
import { useBasket } from '../../../../bff-api/baskets/useBasket';
import {
    hasAssociatedExtendProductItem,
    isDropship,
    isExtend,
    isExtendPostPurchase,
    isGiftRegistry,
    isShippingProtection,
} from '../../../../utils/product-utils';
import { getVariantVariationType } from '../../../../utils/variant-helpers';
import { useBasketProductState } from '../../hooks/useBasketProductState';
import { CulinaryItem } from '../culinary-item';
import { DropshipItem } from './DropshipItem';
import { ExtendItem } from './ExtendItem';
import { GiftCardItem } from './GiftCardItem';
import { GiftRegistryItem } from './GiftRegistryItem';
import { HardGoodItem } from './HardGoodItem';
import { ShippingProtectionItem } from './ShippingProtectionItem';
import type { ProductItem, ProductItemEditor } from '../../../../bff-api/baskets/model';
import type { Product } from '../../../../bff-api/products/model';
import type { CartCulinaryClass } from '../../models/CartCulinaryProductItem';
import type { CulinaryItemProps } from '../culinary-item';
import type { BaseItemProps } from './BaseItem';
import type { ExtendItemProps } from './ExtendItem';
import type { GiftCardItemProps } from './GiftCardItem';
import type { HardGoodItemProps } from './HardGoodItem';

type CartItemGenericProps = Omit<
    BaseItemProps & HardGoodItemProps & ExtendItemProps & CulinaryItemProps & GiftCardItemProps,
    'id' | 'productItem'
>;

// For this dynamic strategy we can't be more specific about the type of the component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Components: Record<string, ComponentType<any>> = {
    HardGood: HardGoodItem,
    EXTEND: ExtendItem,
    GiftRegistry: GiftRegistryItem,
    Dropship: DropshipItem,
    'GiftCard-Physical': GiftCardItem,
    'GiftCard-Electronic': GiftCardItem,
    Culinary: CulinaryItem,
    ShippingProtection: ShippingProtectionItem,
};

export interface CartItemProps {
    productItem: ProductItem;
}

export const CartItem: FC<CartItemProps> = ({ productItem }) => {
    const { data: basket, isError: isBasketError, error: basketError } = useBasket();

    if (isBasketError) {
        throw basketError;
    }

    const { getStateForProduct } = useBasketProductState();
    const { flags, availability, product } = getStateForProduct(productItem.productId) ?? {
        flags: {
            isLimitedQuantityBadge: false,
            isOnlyXLeftBadge: false,
        },
        availability: { availableToSell: 0 },
        product: { isSaleFinal: false, c_isExtendEligible: false } as Product,
    };

    const variationType = useMemo(
        () => capitalize(getVariantVariationType(product?.variationValues ?? {})),
        [product?.variationValues],
    );
    const variation =
        variationType && productItem.c_variationModel
            ? `${variationType}: ${productItem.c_variationModel}`
            : undefined;

    const hasAssociatedExtendPI = useMemo(
        () => hasAssociatedExtendProductItem(productItem, basket?.productItems ?? []),
        [productItem, basket?.productItems],
    );

    const genericProps = {
        productItem,
        product,
        variation,
        isLimitedQuantityBadge: flags?.isLimitedQuantityBadge ?? false,
        isOnlyXLeftBadge: flags?.isOnlyXLeftBadge ?? false,
        isSaleFinal: product?.isSaleFinal,
        availableToSell: availability?.availableToSell ?? 0,
        extendAssociation: productItem.c_ExtendAssociation ?? '',
        isExtendEligible: !!product?.c_isExtendEligible && !hasAssociatedExtendPI,
        isSoftLink: productItem.c_productType === ProductType.HARD_GOOD,
    } satisfies CartItemGenericProps & {
        productItem: ProductItem | ProductItemEditor | CartCulinaryClass;
        isSoftLink?: boolean;
    };

    let componentType: string | undefined = productItem.c_productType?.toString(); // HardGood, GiftCard-Physical, GiftCard-Electronic, Culinary
    componentType = isGiftRegistry(productItem) ? 'GiftRegistry' : componentType;
    componentType = isDropship(productItem) ? 'Dropship' : componentType;
    componentType ||=
        isExtend(productItem) || isExtendPostPurchase(productItem) ? 'EXTEND' : componentType; // Extend[PP]-[12|24|36]

    if (componentType) {
        const Component = Components[componentType];

        return Component ? <Component {...genericProps} /> : null;
    }

    if (isShippingProtection(productItem)) {
        const Component = Components.ShippingProtection;
        return Component ? <Component {...genericProps} /> : null;
    }

    return null;
};
